import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

interface SelectProps {
  options: SelectOption[];
  value?: string | number;
  onChange: (value: string | number | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  searchable?: boolean;
  getSearchText?: (option: SelectOption) => string;
  exactValueMatchWhenQueryMatches?: RegExp;
  getOptionKey?: (option: SelectOption, index: number) => string | number;
  label?: string;
  hint?: string;
  error?: string;
  containerClassName?: string;
  clearable?: boolean;
  inputSize?: 'sm' | 'md' | 'lg';
  triggerClassName?: string;
  isCombobox?: boolean;
  creatable?: boolean;
  dropdownWidth?: number | 'auto' | 'trigger';
}

const fuzzySearch = (query: string, text: string): boolean => {
  if (!query) return true;
  const queryLower = query.toLowerCase();
  const textLower = text.toLowerCase();
  if (textLower.includes(queryLower)) return true;
  let queryIndex = 0;
  for (let i = 0; i < textLower.length && queryIndex < queryLower.length; i++) {
    if (textLower[i] === queryLower[queryIndex]) queryIndex++;
  }
  return queryIndex === queryLower.length;
};

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option...',
  disabled = false,
  required = false,
  className,
  searchable = true,
  clearable = true,
  getSearchText,
  exactValueMatchWhenQueryMatches,
  getOptionKey,
  label,
  hint,
  error,
  containerClassName,
  inputSize = 'md',
  triggerClassName,
  isCombobox = false,
  creatable = false,
  dropdownWidth = 'trigger'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [dropdownRect, setDropdownRect] = useState<{ top: number; bottom: number; left: number; width: number; openUp: boolean } | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const selectedOption = options.find(opt => opt.value == value);
  const isCustomValue = creatable && value !== undefined && !options.some(opt => opt.value == value);
  const trimmedQuery = (searchQuery ?? '').trim();
  const isQueryDialCode = exactValueMatchWhenQueryMatches?.test(trimmedQuery);
  const normalizedDialCode = isQueryDialCode && trimmedQuery
    ? (trimmedQuery.startsWith('+') ? trimmedQuery : '+' + trimmedQuery)
    : null;

  const filteredOptions = searchable && trimmedQuery
    ? isQueryDialCode && normalizedDialCode
      ? options.filter(opt => String(opt.value) === normalizedDialCode)
      : options.filter(opt => {
        const textToSearch = getSearchText ? getSearchText(opt) : opt.label;
        return fuzzySearch(trimmedQuery, textToSearch);
      })
    : options;

  useLayoutEffect(() => {
    if (!isOpen || !selectRef.current) {
      setDropdownRect(null);
      return;
    }
    const rect = selectRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < 250 && spaceAbove > spaceBelow;

    setDropdownRect({
      top: rect.top,
      bottom: rect.bottom,
      left: rect.left,
      width: rect.width,
      openUp,
    });
  }, [isOpen]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      const path = event.composedPath();
      if (selectRef.current && path.includes(selectRef.current)) return;
      if (dropdownRef.current && path.includes(dropdownRef.current)) return;
      if (isCombobox && creatable && searchQuery.trim() && !options.some(opt => String(opt.value) === searchQuery.trim())) {
        onChange(searchQuery.trim());
      }
      setIsOpen(false);
      setSearchQuery('');
    };

    if (isOpen) {
      document.addEventListener('mousedown', handlePointerDown);
      setActiveIndex(0);
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen, isCombobox, creatable, searchQuery, options, onChange]);

  useEffect(() => {
    if (isOpen && activeIndex >= 0 && optionRefs.current[activeIndex]) {
      optionRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [isOpen, activeIndex]);

  const handleSelect = (optionValue: string | number) => {
    if (optionValue != value) onChange(optionValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(undefined);
    setSearchQuery('');
  };

  return (
    <div className={cn('space-y-1.5 w-full relative', containerClassName || className)}>
      {(label || hint) && (
        <div className="flex items-center justify-between gap-2 ml-0.5">
          {label && (
            <label className="text-xs font-semibold text-slate-700">
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
          )}
          {hint && <span className="text-[11px] text-slate-400 font-medium text-right">{hint}</span>}
        </div>
      )}
      <div className={cn("relative", isCombobox && "group/combobox")} ref={selectRef}>
        {isCombobox ? (
          <div className="relative flex items-center" role="combobox" aria-expanded={isOpen} aria-haspopup="listbox" aria-label={label || placeholder}>
            <input
              ref={searchInputRef}
              type="text"
              disabled={disabled}
              placeholder={placeholder}
              value={isOpen ? searchQuery : (selectedOption?.label || (isCustomValue ? String(value) : ''))}
              onChange={(e) => {
                if (!isOpen) setIsOpen(true);
                setSearchQuery(e.target.value);
                setActiveIndex(0);
              }}
              onFocus={() => !disabled && setIsOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setIsOpen(true);
                  setActiveIndex(0);
                } else if (e.key === 'Escape') {
                  setIsOpen(false);
                } else if (e.key === 'Enter' && isCombobox && creatable && searchQuery.trim() && !options.some(opt => String(opt.value) === searchQuery.trim())) {
                  onChange(searchQuery.trim());
                  setIsOpen(false);
                  setSearchQuery('');
                }
              }}
              aria-autocomplete="list"
              aria-controls={isOpen ? 'select-listbox' : undefined}
              aria-activedescendant={isOpen && activeIndex >= 0 ? `select-option-${activeIndex}` : undefined}
              className={cn(
                'w-full border rounded-lg text-left transition-colors',
                'focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500',
                'bg-white border-slate-200 hover:border-slate-300',
                inputSize === 'sm' && 'h-9 px-3 text-xs',
                inputSize === 'md' && 'h-10 px-4 text-sm',
                inputSize === 'lg' && 'h-12 px-5 text-base',
                disabled && 'bg-slate-50 cursor-not-allowed opacity-50',
                error && 'border-rose-300 bg-rose-50 placeholder:text-rose-400',
                'pr-9',
                triggerClassName
              )}
            />
            <div className="absolute right-2.5 flex items-center gap-1">
              {value && !disabled && clearable && (
                <div onClick={handleClear} className="p-0.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 cursor-pointer opacity-0 group-hover/combobox:opacity-100 focus-within:opacity-100 transition-opacity">
                  <X size={13} />
                </div>
              )}
              <ChevronDown size={14} className={cn('text-slate-400 transition-transform cursor-pointer', isOpen && 'rotate-180')} />
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(!isOpen)}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
                e.preventDefault();
                setIsOpen(true);
                setActiveIndex(0);
              } else if (e.key === 'Escape') {
                setIsOpen(false);
              }
            }}
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-label={label || placeholder}
            className={cn(
              'group/trigger w-full border rounded-lg text-left transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500',
              isOpen ? 'border-blue-500 ring-2 ring-blue-500/25' : 'border-slate-200 hover:border-slate-300',
              'bg-white text-slate-800',
              inputSize === 'sm' && 'h-9 px-3 text-xs',
              inputSize === 'md' && 'h-10 px-4 text-sm',
              inputSize === 'lg' && 'h-12 px-5 text-base',
              disabled && 'bg-slate-50 cursor-not-allowed opacity-50',
              error && 'border-rose-300 bg-rose-50',
              'flex items-center justify-between gap-2',
              triggerClassName
            )}
          >
            <span className={cn('flex-1 truncate', !selectedOption && 'text-slate-400')}>
              {selectedOption ? selectedOption.label : placeholder}
            </span>
            <div className="flex items-center gap-1">
              {value && !disabled && clearable && (
                <div onClick={handleClear} className="p-0.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600 transition-all cursor-pointer opacity-0 group-hover/trigger:opacity-100">
                  <X size={13} />
                </div>
              )}
              <ChevronDown size={14} className={cn('text-slate-400 transition-transform', isOpen && 'rotate-180')} />
            </div>
          </button>
        )}

        {isOpen && dropdownRect && createPortal(
          <div
            ref={dropdownRef}
            data-marketing-select-dropdown
            role="listbox"
            id="select-listbox"
            aria-label={label || placeholder}
            className="fixed z-[99999] bg-white border border-slate-200 rounded-lg shadow-[0_6px_20px_-4px_rgba(15,23,42,0.14)] overflow-hidden flex flex-col animate-select-pop"
            style={{
              top: dropdownRect.openUp ? 'auto' : dropdownRect.bottom + 4,
              bottom: dropdownRect.openUp ? window.innerHeight - dropdownRect.top + 4 : 'auto',
              left: dropdownRect.left,
              width: dropdownWidth === 'trigger' ? dropdownRect.width : dropdownWidth === 'auto' ? 'auto' : dropdownWidth,
              minWidth: dropdownWidth === 'trigger' ? dropdownRect.width : 100,
              maxHeight: '300px'
            }}
          >
            {searchable && !isCombobox && (
              <div className="p-1.5 border-b border-slate-100">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setActiveIndex(0);
                    }}
                    placeholder="Search..."
                    className="w-full pl-8 pr-3 h-9 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-500 bg-white placeholder:text-slate-400"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        setActiveIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
                      } else if (e.key === 'ArrowUp') {
                        e.preventDefault();
                        setActiveIndex(prev => Math.max(prev - 1, 0));
                      } else if (e.key === 'Enter' && activeIndex >= 0) {
                        const opt = filteredOptions[activeIndex];
                        if (opt && !opt.disabled) handleSelect(opt.value);
                      } else if (e.key === 'Escape') {
                        setIsOpen(false);
                      }
                    }}
                  />
                </div>
              </div>
            )}
            <div
              className="overflow-y-auto customize-scrollbar py-1 min-h-0 space-y-px"
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActiveIndex(prev => Math.min(prev + 1, filteredOptions.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActiveIndex(prev => Math.max(prev - 1, 0));
                } else if (e.key === 'Enter' && activeIndex >= 0) {
                  const opt = filteredOptions[activeIndex];
                  if (opt && !opt.disabled) handleSelect(opt.value);
                } else if (e.key === 'Escape') {
                  setIsOpen(false);
                }
              }}
            >
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-6 text-xs text-slate-400 text-center">
                  No results found
                </div>
              ) : (
                filteredOptions.map((option, index) => (
                  <button
                    key={getOptionKey ? getOptionKey(option, index) : option.value}
                    id={`select-option-${index}`}
                    ref={el => { optionRefs.current[index] = el; }}
                    type="button"
                    role="option"
                    aria-selected={value == option.value}
                    onClick={() => !option.disabled && handleSelect(option.value)}
                    onMouseEnter={() => setActiveIndex(index)}
                    disabled={option.disabled}
                    className={cn(
                      'w-[calc(100%-8px)] mx-1 px-3 h-9 text-sm text-left transition-colors rounded-md flex items-center justify-between gap-2',
                      value == option.value ? 'bg-blue-50 text-blue-700 font-medium' : 'text-slate-700',
                      option.disabled && 'opacity-40 cursor-not-allowed',
                      activeIndex === index && !option.disabled && value != option.value && 'bg-slate-100'
                    )}
                  >
                    <span className="truncate">{option.label}</span>
                    {value == option.value && <Check size={14} className="text-blue-600 shrink-0" />}
                  </button>
                ))
              )}
            </div>
          </div>,
          document.body
        )}
      </div>
      {error && (
        <p className="text-[11px] text-rose-500 font-medium ml-0.5 mt-1">
          {error}
        </p>
      )}
    </div>
  );
};
